if (!window.__dcCompleteKitCalculatorLoaded) {
  window.__dcCompleteKitCalculatorLoaded = true;

/* ==========================================================================
   Complete Kit calculator

   Buys exactly 1 Complete Kit (tools included) for the first 200 sq ft,
   then tops off any extra area with the matched refill bundle (same
   stain/sealer, no tools) and — where applicable — extra degreaser, so
   the shopper never ends up buying duplicate tools.

   Same math as the original EverStain custom_liquid calculator, just
   generalized into a reusable block. Shares the .dc-calculator2 visual
   language and the recipe table markup with custom-calculator-v2, but is
   a fully separate, self-contained engine (own cart logic, own variant
   sync) so neither can break the other.
   ========================================================================== */

const TYPE_CONFIG = {
  completeKit200: { coverage: 200, wantsDegreaser: true },
  completeKit200NoDegreaser: { coverage: 200, wantsDegreaser: false }
};

const getNumber = v => parseFloat(v) || 0;
const money = cents => '$' + ((parseFloat(cents) || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function getProductOptions() {
  return Array.isArray(window.PRODUCT_OPTIONS) ? window.PRODUCT_OPTIONS : [];
}

// Scoped to the calculator's own section — see custom-product-calculator-v2.js
// for why this can't be an unscoped `input[name="id"]` lookup (the sticky
// add-to-cart bar renders its own duplicate product form).
function getNativeVariant(calc, variants) {
  let sectionId = null;
  try {
    sectionId = window.FoxTheme?.utils?.getSectionId ? FoxTheme.utils.getSectionId(calc) : null;
  } catch (e) { /* fall back to an unscoped lookup */ }

  const scope = sectionId ? document.querySelector(`#product-form-${sectionId}`) : null;
  const els = (scope || document).querySelectorAll('input[name="id"], select[name="id"]');

  for (const el of els) {
    const id = parseInt(el.value, 10);
    const variant = variants.find(v => v.id === id);
    if (variant) return variant;
  }
  return null;
}

async function postToCart(items) {
  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items })
  });

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.description || 'Failed to add to cart');
  }

  const data = await response.json();
  document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  window.dispatchEvent(new Event('cart:updated'));
  document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { items: data } }));
  return data;
}

async function updateCartUI() {
  try {
    const { item_count } = await fetch('/cart.js').then(r => r.json());

    document.querySelectorAll('.cart-count, cart-count, [data-cart-count]').forEach(counter => {
      const oldCount = parseInt(counter.textContent) || 0;
      counter.textContent = item_count;
      counter.setAttribute('aria-label', `${item_count} items`);

      if (item_count > 0) {
        counter.style.display = '';
        counter.removeAttribute('hidden');
        counter.classList.remove('hidden');
      }

      if (item_count > oldCount) {
        counter.classList.add('cart-count--updated');
        setTimeout(() => counter.classList.remove('cart-count--updated'), 600);
      }
    });

    // Re-render the cart drawer through the theme's own component — this is
    // what the original EverStain script skipped, which is what left its
    // drawer out of sync.
    const cartDrawer = document.querySelector('#CartDrawer');
    if (!cartDrawer) return;

    if (typeof cartDrawer.onCartRefresh === 'function') {
      await cartDrawer.onCartRefresh({ detail: { open: true } });
    }

    await new Promise(resolve => setTimeout(resolve, 400));

    const closeBtn = cartDrawer.querySelector('.drawer__close-btn');
    if (closeBtn) {
      const freshBtn = closeBtn.cloneNode(true);
      closeBtn.replaceWith(freshBtn);
      freshBtn.addEventListener('click', () => {
        if (typeof cartDrawer.hide === 'function') {
          cartDrawer.hide();
        } else {
          cartDrawer.removeAttribute('open');
          cartDrawer.setAttribute('aria-hidden', 'true');
          document.body.classList.remove('overflow-hidden');
        }
      });
    }
  } catch (error) {
    console.error('❌ Error updating cart UI:', error);
  }
}

function applyThemeAccent(calc) {
  try {
    const sels = [
      'product-form button[type="submit"]', '.product-form__submit',
      'button[name="add"]', 'form[action*="/cart/add"] button[type="submit"]',
      'form[action*="/cart/add"] [type="submit"]'
    ];
    for (const sel of sels) {
      const btn = document.querySelector(sel);
      if (!btn) continue;
      const color = getComputedStyle(btn).backgroundColor;
      if (color && color !== 'rgba(0, 0, 0, 0)' && color !== 'transparent') {
        calc.style.setProperty('--dc-calc2-accent', color);
        return;
      }
    }
  } catch (e) { /* fall back to the CSS default */ }
}

/* ==========================================================================
   Init
   ========================================================================== */

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-ck-calculator]').forEach(initCalculator);
});

function initCalculator(calc) {
  const config = TYPE_CONFIG[calc.dataset.ckType] || TYPE_CONFIG.completeKit200;
  const fixedOptionValue = calc.dataset.ckFixedOptionValue || null;
  const $ = sel => calc.querySelector(sel);

  const dataEl = $('[data-ck-data]');
  let data;
  try {
    data = JSON.parse(dataEl.textContent);
  } catch (e) {
    console.error('❌ Complete Kit calculator: could not parse data payload', e);
    return;
  }

  applyThemeAccent(calc);

  const labels = data.labels || {};
  const kitVariants = (data.kit && data.kit.variants) || [];
  const refillVariants = (data.refill && data.refill.variants) || [];
  const degreaser = data.degreaser || null;
  const productOptions = getProductOptions(); // shared with the kit product's own variant picker

  const areaEl = $('[data-result="area"]');
  const recipeBody = $('[data-result="recipe"]');
  const grandTotalEl = $('[data-result="grand-total"]');
  const addToCartBtn = $('[data-add-to-cart]');
  const variantOptionsWrapper = $('[data-variant-options]');
  const variantOptionsHeading = $('[data-variant-options-heading]');

  const inputs = {
    length: $('[data-input="length"]'),
    width: $('[data-input="width"]'),
    coats: $('[data-input="coats"]')
  };

  let calcSelectedOptions = {};
  let userOverrodeOptions = false;
  let lastNativeVariantId = null;
  let currentRecipe = [];

  setupCoats();
  setupVariantOptions();
  if (addToCartBtn) setBtnState('Add Everything to Cart');
  renderEmptyRecipe();
  calculate();

  calc.querySelectorAll('input, select').forEach(el => {
    el.addEventListener('input', calculate);
    el.addEventListener('change', calculate);
  });

  if (addToCartBtn) addToCartBtn.addEventListener('click', handleAddToCart);

  /* ============================
     Setup
     ============================ */

  function setupCoats() {
    if (!inputs.coats) return;
    const options = [
      { label: '2 Coats (recommended)', value: 1 },
      { label: '4 Coats', value: 2 }
    ];
    inputs.coats.innerHTML = options.map(o => `<option value="${o.value}">${o.label}</option>`).join('');
    inputs.coats.value = 1;
  }

  function setupVariantOptions() {
    // Fixed-variant products (e.g. sized by "1 Gallon" / "5 Gallons" rather
    // than Color/Sealer) always resolve to fixedOptionValue — no picker,
    // no native-picker sync needed.
    if (fixedOptionValue) return;

    if (!variantOptionsWrapper || !productOptions.length) return;
    const pickable = productOptions.filter(opt => opt.values && opt.values.length > 1);
    if (!pickable.length) return;

    if (variantOptionsHeading) variantOptionsHeading.hidden = false;
    variantOptionsWrapper.hidden = false;
    variantOptionsWrapper.innerHTML = pickable.map((opt, i) => `
      <div class="dc-calculator2__variant-option">
        <select data-variant-option="${opt.name}" id="ck-opt-${i}-${Math.random().toString(36).slice(2, 8)}" aria-label="${opt.name}">
          ${opt.values.map(v => `<option value="${v}">${v}</option>`).join('')}
        </select>
      </div>
    `).join('');

    variantOptionsWrapper.querySelectorAll('[data-variant-option]').forEach(select => {
      select.addEventListener('change', () => {
        userOverrodeOptions = true;
        calcSelectedOptions[select.dataset.variantOption] = select.value;
        calculate();
      });
    });

    syncFromNativeVariant();
    // The native picker can trigger a full section re-render on change;
    // poll instead of trying to hook into that flow directly.
    setInterval(syncFromNativeVariant, 600);
  }

  function syncFromNativeVariant() {
    if (!kitVariants.length) return;
    const nativeVariant = getNativeVariant(calc, kitVariants);
    if (!nativeVariant) return;

    if (nativeVariant.id !== lastNativeVariantId) {
      // The shopper just picked something in the page's own variant picker
      // (or this is the first run) — that always wins over a previous
      // calculator-only selection.
      lastNativeVariantId = nativeVariant.id;
      userOverrodeOptions = false;
    }

    if (userOverrodeOptions) return;

    productOptions.forEach((opt, i) => {
      const value = nativeVariant[`option${i + 1}`];
      if (value == null) return;
      calcSelectedOptions[opt.name] = value;
      const select = variantOptionsWrapper?.querySelector(`[data-variant-option="${CSS.escape(opt.name)}"]`);
      if (select && select.value !== value) select.value = value;
    });

    calculate();
  }

  function findVariantByOptions(variants) {
    if (!variants.length) return null;

    if (fixedOptionValue) {
      return variants.find(v => v.option1 === fixedOptionValue)
        || variants.find(v => valuesLooselyMatch(v.option1, fixedOptionValue))
        || null;
    }

    if (!productOptions.length) return variants[0];

    const exact = variants.find(v =>
      productOptions.every((opt, i) => {
        const selected = calcSelectedOptions[opt.name];
        return selected == null || v[`option${i + 1}`] === selected;
      })
    );
    if (exact) return exact;

    // Fall back to a looser brand+finish match: the kit and its refill
    // product are separate Shopify products, and their option values
    // sometimes drift slightly (e.g. "HydroCryl™ High Gloss" on the kit vs
    // "HydroCryl™ Gloss" on the refill bundle for the same finish).
    return variants.find(v =>
      productOptions.every((opt, i) => {
        const selected = calcSelectedOptions[opt.name];
        return selected == null || valuesLooselyMatch(v[`option${i + 1}`], selected);
      })
    ) || null;
  }

  function normalizeOptionValue(value) {
    return (value || '').toLowerCase().replace(/[™®]/g, '').replace(/\s+/g, ' ').trim();
  }

  function valuesLooselyMatch(a, b) {
    const na = normalizeOptionValue(a);
    const nb = normalizeOptionValue(b);
    if (!na || !nb) return false;
    if (na === nb) return true;

    // Same brand (first word) and same finish keyword (Gloss/Satin/Matte),
    // ignoring extra descriptive words like "High".
    const brandA = na.split(' ')[0];
    const brandB = nb.split(' ')[0];
    if (brandA !== brandB) return false;

    const finishes = ['gloss', 'satin', 'matte'];
    const finishA = finishes.find(f => na.includes(f));
    const finishB = finishes.find(f => nb.includes(f));
    return Boolean(finishA) && finishA === finishB;
  }

  // Fixed-variant refills (Size only, no Color/Sealer — e.g. CitrusEtch's
  // Etching System, which sells both "1 Gallon" and "5 Gallons") can cover
  // more than one gallon size.
  function parseGallonSize(optionValue) {
    const match = /([\d.]+)\s*gallon/i.exec(optionValue || '');
    return match ? parseFloat(match[1]) : null;
  }

  function getGallonSizes(variants) {
    const seen = new Map();
    variants.forEach(v => {
      const value = parseGallonSize(v.option1);
      if (value != null && !seen.has(value)) seen.set(value, v);
    });
    return [...seen.entries()]
      .map(([value, variant]) => ({ value, variant }))
      .sort((a, b) => b.value - a.value);
  }

  // Cheapest way to cover at least `gallonsNeeded` gallons, buying any mix
  // of the available sizes (repeats allowed). Picking by volume alone would
  // never recommend a 5-Gallon jug for a 4-gallon need since it "doesn't
  // fit" — but the 5-Gallon jug is $59.95 flat while four 1-Gallon bottles
  // run $119.80, so overshooting by a gallon is the better deal. This is a
  // standard unbounded-knapsack ("coin change") minimization over exact
  // gallon totals, not a greedy volume pick.
  function buildSizeCombo(variants, gallonsNeeded) {
    const sizes = getGallonSizes(variants);
    if (!sizes.length || gallonsNeeded <= 0) return [];

    const target = Math.ceil(gallonsNeeded);
    const maxSize = Math.max(...sizes.map(size => size.value));
    // Overshooting an optimal combo past (maxSize - 1) gallons above the
    // target is never worth it: any unit that could be dropped while
    // staying at/above target would only lower the cost, so the cheapest
    // total always lands within one "extra" unit of the largest size.
    const limit = target + maxSize - 1;

    const cheapestCost = [0];
    const sizeUsedFor = [null];
    for (let gallons = 1; gallons <= limit; gallons++) {
      let best = Infinity;
      let bestSize = null;
      sizes.forEach(size => {
        const remainder = gallons - size.value;
        if (remainder < 0) return;
        const cost = cheapestCost[remainder] + (parseFloat(size.variant.price) || 0);
        if (cost < best) {
          best = cost;
          bestSize = size;
        }
      });
      cheapestCost.push(best);
      sizeUsedFor.push(bestSize);
    }

    let bestTotal = target;
    for (let gallons = target + 1; gallons <= limit; gallons++) {
      if (cheapestCost[gallons] < cheapestCost[bestTotal]) bestTotal = gallons;
    }

    const quantityBySize = new Map();
    for (let gallons = bestTotal; gallons > 0 && sizeUsedFor[gallons]; ) {
      const size = sizeUsedFor[gallons];
      quantityBySize.set(size.value, (quantityBySize.get(size.value) || 0) + 1);
      gallons -= size.value;
    }

    return sizes
      .filter(size => quantityBySize.has(size.value))
      .map(size => ({ variant: size.variant, quantity: quantityBySize.get(size.value) }));
  }

  function comboCost(combo) {
    return combo.reduce((sum, { variant, quantity }) => sum + quantity * (parseFloat(variant?.price) || 0), 0);
  }

  // Some fixed-variant kits (Size only) sell the SAME tools at every size —
  // e.g. CitrusEtch's Application System is $64.95 at 1 Gallon or $79.95 at
  // 5 Gallons, no extra sprayer either way. Buying the bigger kit outright
  // can beat a small kit plus a refill (1×5Gal kit at $79.95 vs. a 1Gal kit
  // + a 5Gal refill at $124.90 for the same 5 gallons). Try each kit size
  // exactly once — always exactly 1 kit, never 2 — and keep whichever kit
  // size + refill combo covers the total for the least money.
  function chooseKitAndRefillCombo(unitsNeeded) {
    const kitSizes = getGallonSizes(kitVariants);
    if (!kitSizes.length) return null;

    let best = null;
    kitSizes.forEach(kitSize => {
      const remaining = Math.max(0, unitsNeeded - kitSize.value);
      const refillCombo = buildSizeCombo(refillVariants, remaining);
      const cost = (parseFloat(kitSize.variant.price) || 0) + comboCost(refillCombo);
      if (!best || cost < best.cost - 1e-9) {
        best = { kitVariant: kitSize.variant, refillCombo, cost };
      }
    });

    return best;
  }

  /* ============================
     Calculations
     ============================ */

  function getArea() {
    return getNumber(inputs.length?.value) * getNumber(inputs.width?.value);
  }

  function quantities(area, coatsFactor) {
    if (!area || area <= 0) return { area: 0, kit: 0, refill: 0, degreaser: 0 };
    const areaBlocks = Math.ceil(area / config.coverage);
    const units = Math.ceil((area * coatsFactor) / config.coverage);
    return {
      area,
      kit: 1,
      refill: Math.max(0, units - 1),
      degreaser: config.wantsDegreaser ? Math.max(0, areaBlocks - 1) : 0
    };
  }

  function calculate() {
    const area = getArea();
    if (areaEl) areaEl.textContent = area ? Math.round(area).toLocaleString('en-US') : '0';
    const coatsFactor = getNumber(inputs.coats?.value) || 1;
    renderRecipe(quantities(area, coatsFactor));
  }

  /* ============================
     Rendering
     ============================ */

  function lineLabel(baseLabel, variant, appendOptions) {
    if (!appendOptions || !variant) return baseLabel;
    const opts = productOptions
      .map((opt, i) => variant[`option${i + 1}`])
      .filter(v => v && v !== 'Default Title');
    return opts.length ? `${baseLabel} — ${opts.join(' / ')}` : baseLabel;
  }

  function renderEmptyRecipe() {
    if (!recipeBody) return;
    recipeBody.innerHTML = '<tr><td colspan="3" class="dc-calculator2__empty-message">Enter your dimensions to see exactly what you need.</td></tr>';
    if (grandTotalEl) grandTotalEl.textContent = '—';
    currentRecipe = [];
    updateAddToCartButton();
  }

  function renderRecipe(q) {
    if (!recipeBody) return;
    if (!q.area) {
      renderEmptyRecipe();
      return;
    }

    let kitVariant;
    let refillCombo;

    // Only fixed-variant products (Size only) ever need this — for
    // everything else the kit and refill are still matched by Color/Sealer,
    // same as before.
    const choice = fixedOptionValue ? chooseKitAndRefillCombo(q.kit + q.refill) : null;
    if (choice) {
      kitVariant = choice.kitVariant;
      refillCombo = choice.refillCombo;
    } else {
      kitVariant = findVariantByOptions(kitVariants);
      refillCombo = [{ variant: findVariantByOptions(refillVariants), quantity: q.refill }];
    }

    const rows = [];
    currentRecipe = [];
    let grandTotalCents = 0;
    let pricingComplete = true;

    function pushRow(variant, qty, fallbackLabel, labelKey, appendOptions) {
      if (qty <= 0) return;
      const label = lineLabel(labels[labelKey] || fallbackLabel, variant, appendOptions);
      let subtotalText = '—';
      if (variant) {
        const lineCents = (parseFloat(variant.price) || 0) * qty;
        grandTotalCents += lineCents;
        subtotalText = money(lineCents);
      } else {
        pricingComplete = false;
      }
      currentRecipe.push({ id: variant ? variant.id : null, quantity: qty });
      rows.push(`<tr><td>${label}</td><td>${qty}</td><td>${subtotalText}</td></tr>`);
    }

    pushRow(kitVariant, q.kit, 'Complete Kit', 'kit', true);
    refillCombo.forEach(({ variant, quantity }) => pushRow(variant, quantity, 'Refill Bundle', 'refill', true));
    if (config.wantsDegreaser) pushRow(degreaser, q.degreaser, 'Degreaser', 'degreaser', false);

    recipeBody.innerHTML = rows.join('');
    if (grandTotalEl) grandTotalEl.textContent = pricingComplete ? money(grandTotalCents) : '—';

    updateAddToCartButton();
    checkAvailability(kitVariant, refillCombo, q);
  }

  function updateAddToCartButton() {
    if (!addToCartBtn) return;
    addToCartBtn.disabled = currentRecipe.length === 0;
  }

  /* ============================
     Availability
     ============================ */

  function checkAvailability(kitVariant, refillCombo, q) {
    if (!addToCartBtn || !currentRecipe.length) return;

    const refillUnavailable = q.refill > 0 && (
      !refillCombo.length || refillCombo.some(({ variant }) => !variant || variant.available === false)
    );

    const unavailable =
      (q.kit > 0 && (!kitVariant || kitVariant.available === false)) ||
      refillUnavailable ||
      (config.wantsDegreaser && q.degreaser > 0 && (!degreaser || degreaser.available === false));

    unavailable ? setOutOfStock() : setInStock();
  }

  function setOutOfStock() {
    if (!addToCartBtn) return;
    addToCartBtn.disabled = true;
    addToCartBtn.style.opacity = '0.5';
    addToCartBtn.style.cursor = 'not-allowed';
    setBtnState('Out of Stock');
  }

  function setInStock() {
    if (!addToCartBtn) return;
    addToCartBtn.disabled = false;
    addToCartBtn.style.opacity = '';
    addToCartBtn.style.cursor = '';
    setBtnState('Add Everything to Cart');
  }

  /* ============================
     Add to Cart
     ============================ */

  function setBtnState(text, color = '') {
    const btnText = addToCartBtn.querySelector('.btn__text');
    if (!btnText) return;
    btnText.textContent = text;
    btnText.style.color = color;
  }

  function resetBtn(delay) {
    setTimeout(() => {
      setBtnState('Add Everything to Cart');
      addToCartBtn.disabled = false;
    }, delay);
  }

  async function handleAddToCart() {
    if (!addToCartBtn || !currentRecipe.length) return;

    addToCartBtn.disabled = true;
    addToCartBtn.classList.add('btn--loading');

    try {
      const items = currentRecipe
        .filter(item => item.id)
        .map(item => ({ id: item.id, quantity: item.quantity }));

      if (!items.length) throw new Error('No valid variants found.');

      await postToCart(items);
      await updateCartUI();

      setBtnState('Added to Cart!');
      resetBtn(2000);

    } catch (error) {
      console.error('❌ Error:', error);
      setBtnState('Error - Try Again', 'var(--color-error, #ff1100)');
      resetBtn(3000);

    } finally {
      addToCartBtn.classList.remove('btn--loading');
    }
  }
}

} // end window.__dcCompleteKitCalculatorLoaded guard
